import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import {
  type ChatChannelCategory,
  getChatCategoryLabelResource,
  getChatChannelCategoriesForRole,
} from "Components/Chat/chatChannelCategories";
import { useCurrentUserAccessRole } from "Hooks/useCurrentUserAccessRole";
import { useResource } from "Translations/Resources";

type Props = {
  value: ChatChannelCategory;
  onChange: (value: ChatChannelCategory) => void;
  pendingRequestCount: number;
};

const CountBadge = ({ count }: { count: number }) => (
  <Box
    component="span"
    sx={{
      ml: 1,
      minWidth: 18,
      height: 18,
      borderRadius: "9px",
      backgroundColor: count > 0 ? "error.main" : "action.disabled",
      color: "common.white",
      fontSize: 11,
      fontWeight: 700,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      lineHeight: 1,
    }}
  >
    {count}
  </Box>
);

export const ChatCategoryFilter = ({
  value,
  onChange,
  pendingRequestCount,
}: Props) => {
  const { t } = useResource();
  const roleCode = useCurrentUserAccessRole();
  const categories = getChatChannelCategoriesForRole(roleCode);

  return (
    <Box flexShrink={0}>
      <ToggleButtonGroup
        value={value}
        exclusive
        onChange={(_, newValue) => {
          if (newValue !== null) {
            onChange(newValue);
          }
        }}
        size="small"
        fullWidth
        sx={{
          flexWrap: "nowrap",
          gap: "8px",
          justifyContent: "space-around",
          "& .MuiToggleButtonGroup-grouped": {
            borderRadius: "8px",
            px: 2,
            py: 0.5,
            textTransform: "none",
            whiteSpace: "nowrap",
            border: "none",
            backgroundColor: "grey.100",
            "&:not(:first-of-type)": {
              marginLeft: 0,
            },
            "&.Mui-selected": {
              backgroundColor: "primary.main",
              color: "primary.contrastText",
              "&:hover": {
                backgroundColor: "primary.main",
              },
            },
          },
        }}
      >
        {categories.map((category) => (
          <ToggleButton key={category} value={category}>
            {t(getChatCategoryLabelResource(category, roleCode))}
            {category === "Requests" && (
              <CountBadge count={pendingRequestCount} />
            )}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};
